import { useEffect, useMemo, useState } from 'react'
import { Box, HStack, Slider, Text } from '@chakra-ui/react'
import moment from 'moment'

type Props = {
    range: {
        start: Date
        stop: Date
    }
    start: Date
    stop: Date
    logTime?: boolean
    snap?: boolean
    onChange?: (range: { start: Date; stop: Date }) => void
}

const SLIDER_MIN = 0
const SLIDER_MAX = 1000
const EXPONENT = 2 // 🔥 tweak this (2–6)

export const DateRangeSlider = ({
    range,
    start,
    stop,
    logTime = false,
    snap = false,
    onChange
}: Props) => {
    const minTime = Math.min(range.start.getTime(), range.stop.getTime())
    const maxTime = Math.max(range.start.getTime(), range.stop.getTime())

    const validRange =
        Number.isFinite(minTime) && Number.isFinite(maxTime) && minTime > 0 && maxTime > minTime

    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v))

    const toSlider = useMemo(() => {
        return (date: Date): number => {
            if (!validRange) return SLIDER_MIN

            const t = clamp(date.getTime(), minTime, maxTime)

            if (!logTime) {
                return clamp(
                    ((t - minTime) / (maxTime - minTime)) * SLIDER_MAX,
                    SLIDER_MIN,
                    SLIDER_MAX
                )
            }

            const linear = (t - minTime) / (maxTime - minTime)
            const p = 1 - Math.pow(1 - linear, 1 / EXPONENT)

            return clamp(p * SLIDER_MAX, SLIDER_MIN, SLIDER_MAX)
        }
    }, [minTime, maxTime, logTime, validRange])

    const fromSlider = useMemo(() => {
        return (value: number): Date => {
            if (!validRange) return new Date()

            const v = clamp(value, SLIDER_MIN, SLIDER_MAX)

            let t: number

            if (!logTime) {
                t = minTime + (v / SLIDER_MAX) * (maxTime - minTime)
            } else {
                const p = v / SLIDER_MAX
                const curved = 1 - Math.pow(1 - p, EXPONENT)
                t = minTime + curved * (maxTime - minTime)
            }

            if (snap) {
                t = snapToNiceTime(t)
            }

            return new Date(clamp(t, minTime, maxTime))
        }
    }, [minTime, maxTime, logTime, snap, validRange])

    const safeStart = Math.min(start.getTime(), stop.getTime())
    const safeStop = Math.max(start.getTime(), stop.getTime())

    const [value, setValue] = useState<number[]>([
        toSlider(new Date(safeStart)),
        toSlider(new Date(safeStop))
    ])

    useEffect(() => {
        setValue([toSlider(new Date(safeStart)), toSlider(new Date(safeStop))])
    }, [safeStart, safeStop, toSlider])

    if (!validRange) {
        return null
    }

    const handleChange = (details: { value: number[] }) => {
        let [a, b] = details.value

        if (a >= b) {
            b = a + 1
        }

        const next = [a, b]
        setValue(next)

        onChange?.({
            start: fromSlider(next[0]),
            stop: fromSlider(next[1])
        })
    }

    const selectedStart = fromSlider(value[0])
    const selectedStop = fromSlider(value[1])

    return (
        <Box width="100%">
            <HStack justify="space-between" mb={2}>
                <Text fontSize="sm">{formatDate(selectedStart)}</Text>
                <Text fontSize="sm">
                    {moment.duration(Math.abs(moment(selectedStop).diff(selectedStart))).humanize()}
                </Text>
                <Text fontSize="sm">{formatDate(selectedStop)}</Text>
            </HStack>

            <Slider.Root
                min={SLIDER_MIN}
                max={SLIDER_MAX}
                step={1}
                value={value}
                onValueChange={handleChange}
                minStepsBetweenThumbs={1}
                thumbCollisionBehavior="push"
            >
                <Slider.Control>
                    <Slider.Track>
                        <Slider.Range />
                    </Slider.Track>
                    <Slider.Thumbs />
                </Slider.Control>
            </Slider.Root>
        </Box>
    )
}

function snapToNiceTime(time: number): number {
    const hour = 60 * 60 * 1000
    return Math.round(time / hour) * hour
}

function formatDate(date: Date): string {
    return moment(date).format('YYYY-MM-DD HH:mm')
}
